import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  ShoppingCart,
  PackageCheck,
  Undo2,
  Package,
  BarChart3,
  ChevronLeft,
  Menu,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/seller", icon: ShoppingCart, label: "Заказы", exact: true as const },
  { to: "/seller/delivery", icon: PackageCheck, label: "Выдача", exact: false as const },
  { to: "/seller/returns", icon: Undo2, label: "Возврат", exact: false as const },
  { to: "/seller/stock", icon: Package, label: "Остатки", exact: false as const },
  { to: "/seller/report", icon: BarChart3, label: "Отчёт", exact: false as const },
];

export default function SellerLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  function isActive(to: string, exact?: boolean) {
    return exact
      ? location.pathname === to
      : location.pathname.startsWith(to);
  }

  function navLink(
    { to, icon: Icon, label, exact }: (typeof NAV_ITEMS)[number],
    opts?: { onClick?: () => void; showLabel?: boolean },
  ) {
    const active = isActive(to, exact);
    return (
      <Link
        key={to}
        to={to}
        onClick={opts?.onClick}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          active
            ? "bg-primary-light text-primary-700"
            : "text-text-secondary hover:bg-gray-100 hover:text-text",
        )}
        title={opts?.showLabel === false ? label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {opts?.showLabel !== false && <span>{label}</span>}
      </Link>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar — desktop */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r border-border bg-surface transition-all duration-200",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <span className="font-bold text-lg">Продавец</span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-text-secondary"
            aria-label="Свернуть боковую панель"
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 transition-transform",
                collapsed && "rotate-180",
              )}
            />
          </button>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-1">
          {NAV_ITEMS.map((item) =>
            navLink(item, { showLabel: !collapsed }),
          )}
        </nav>

        <div className="px-4 py-3 border-t border-border">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {!collapsed && "На сайт"}
          </Link>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-surface border-r border-border animate-fade-in">
            <div className="flex h-16 items-center justify-between px-4 border-b border-border">
              <span className="font-bold text-lg">Продавец</span>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Закрыть меню"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <nav className="py-4 px-2 space-y-1">
              {NAV_ITEMS.map((item) =>
                navLink(item, { onClick: () => setMobileOpen(false) }),
              )}
            </nav>
            <div className="px-4 py-3 border-t border-border">
              <Link
                to="/"
                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text"
                onClick={() => setMobileOpen(false)}
              >
                <ArrowLeft className="h-4 w-4" />
                На сайт
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex h-16 items-center gap-4 border-b border-border bg-surface px-4 md:px-6">
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Открыть меню"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">
            {NAV_ITEMS.find((item) => isActive(item.to, item.exact))
              ?.label ?? "Панель продавца"}
          </h1>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
